import { User } from '@futrli-sdk/people';
import { UserProperty } from '@futrli-sdk/users';
import { API_PATHS, REQUEST_METHODS, ROUTE_PARAMS, interpolatePathParams } from '@futrli/shared-services';

import { APIClients } from '@futrli/shared-services';

/**
 * Returns the list of user properties from the api.
 *
 * @returns {Promise<UserProperty[]>}
 */
export const getUserProperties = async (): Promise<UserProperty[]> => {
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.GET,
        url: API_PATHS.USER_PROPERTIES
      }
    }
  });

  return response.data.data;
};

/**
 * Creates the user properties passes in the array and any of them exists, then updates it.
 * @param {UserProperty[]} property
 */
export const upsertUserProperties = async (property: UserProperty[]) => {
  await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: API_PATHS.USER_PROPERTIES,
        data: property
      }
    }
  });
};

/**
 * Updates a given user property, if it does not exist, then creates it.
 * @param {UserProperty} property
 */
export const updateUserProperty = async (property: UserProperty) => {
  await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: API_PATHS.USER_PROPERTIES,
        data: property
      }
    }
  });
};

/**
 * Deletes a given user property.
 *
 * @param {UserProperty} property
 */
export const deleteUserProperty = async (property: Pick<UserProperty, 'property'>) => {
  await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.DELETE,
        url: API_PATHS.USER_PROPERTIES,
        data: property
      }
    }
  });
};

/**
 * Fetches and returns a users details
 *
 * @function
 * @name getUserDetails
 * @param {string} userId - User Id.
 * @returns {Promise<User>} - Full details for the user
 */
export const getUserDetails = async (userId: string): Promise<Partial<User>> => {
  const path = interpolatePathParams(API_PATHS.USER_DETAILS, { [ROUTE_PARAMS.USER_ID]: userId });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.GET,
        url: path
      }
    }
  });

  return response.data;
};

/**
 * Partial update of a users details
 *
 * @function
 * @name patchUserDetails
 * @param {string} userId - User Id.
 * @param {object} userDetails - User details.
 * @returns {Promise<User>} - Full details updated details for the user
 */
export const patchUserDetails = async (userId: string, userDetails: Partial<User>): Promise<Partial<User>> => {
  const path = interpolatePathParams(API_PATHS.USER_DETAILS, { [ROUTE_PARAMS.USER_ID]: userId });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PATCH,
        url: path,
        data: userDetails
      }
    }
  });

  return response.data;
};
